import React from 'react';

export interface CardProps {
  children: React.ReactNode;
  variant?: 'glassmorphism' | 'elevated' | 'flat';
  padding?: 'none' | 'sm' | 'md' | 'lg' | 'xl';
  shadow?: 'none' | 'sm' | 'md' | 'lg' | 'xl' | 'custom';
  hover?: boolean;
  animation?: boolean;
  animationDelay?: number;
  className?: string;
  onClick?: () => void;
}

/**
 * Card Component v2 - FacturXPro Design System
 *
 * Carte avec effet glassmorphism, animations et hover effects
 * Basé sur le nouveau design Scandinave/Volvo avec Teal
 *
 * @example
 * ```tsx
 * // Glassmorphism avec animation
 * <Card variant="glassmorphism" hover animation animationDelay={200}>
 *   <h3>Titre</h3>
 *   <p>Contenu...</p>
 * </Card>
 *
 * // Carte élevée sans animation
 * <Card variant="elevated" padding="lg">
 *   <p>Contenu...</p>
 * </Card>
 * ```
 *
 * Référence: DESIGN_SYSTEM.md section "Carte Standard"
 */
export const Card: React.FC<CardProps> = ({
  children,
  variant = 'glassmorphism',
  padding = 'lg',
  shadow = 'custom',
  hover = false,
  animation = false,
  animationDelay = 0,
  className = '',
  onClick,
}) => {
  // Variant classes
  const variantClasses = {
    glassmorphism: 'bg-white/20 backdrop-blur-xl border border-white/30',
    elevated: 'bg-white border border-gray-200',
    flat: 'bg-white border-none',
  };

  // Padding classes
  const paddingClasses = {
    none: 'p-0',
    sm: 'p-4',
    md: 'p-6',
    lg: 'p-8',
    xl: 'p-10',
  };

  // Shadow classes
  const shadowClasses = {
    none: 'shadow-none',
    sm: 'shadow-sm',
    md: 'shadow-md',
    lg: 'shadow-lg',
    xl: 'shadow-xl',
    custom: 'shadow-xl shadow-gray-300/10',
  };

  // Hover classes
  const hoverClasses = hover
    ? 'hover:bg-white/70 hover:shadow-2xl hover:-translate-y-1 transition-all duration-500 cursor-pointer'
    : 'transition-all duration-300';

  // Animation classes
  const animationClasses = animation ? 'animate-fade-in-up' : '';

  // Custom shadow style
  const customShadowStyle =
    shadow === 'custom'
      ? {
          boxShadow:
            '0 10px 40px rgba(0,0,0,0.02), 0 2px 15px rgba(0,0,0,0.01)',
          animationDelay: animation ? `${animationDelay}ms` : undefined,
        }
      : {
          animationDelay: animation ? `${animationDelay}ms` : undefined,
        };

  return (
    <div
      className={`
        ${variantClasses[variant]}
        ${paddingClasses[padding]}
        ${shadowClasses[shadow]}
        ${hoverClasses}
        ${animationClasses}
        rounded-3xl
        ${className}
      `.trim()}
      style={customShadowStyle}
      onClick={onClick}
    >
      {children}
    </div>
  );
};

// Sub-components for compatibility with ui/Card
interface CardHeaderProps {
  children: React.ReactNode;
  className?: string;
}

export const CardHeader: React.FC<CardHeaderProps> = ({ children, className = '' }) => {
  return <div className={`mb-4 ${className}`}>{children}</div>;
};

interface CardTitleProps {
  children: React.ReactNode;
  className?: string;
}

export const CardTitle: React.FC<CardTitleProps> = ({ children, className = '' }) => {
  return <h3 className={`text-lg font-semibold text-gray-900 ${className}`}>{children}</h3>;
};

interface CardContentProps {
  children: React.ReactNode;
  className?: string;
}

export const CardContent: React.FC<CardContentProps> = ({ children, className = '' }) => {
  return <div className={`${className}`}>{children}</div>;
};

interface CardFooterProps {
  children: React.ReactNode;
  className?: string;
}

export const CardFooter: React.FC<CardFooterProps> = ({ children, className = '' }) => {
  return <div className={`mt-4 pt-4 border-t border-gray-100 ${className}`}>{children}</div>;
};

export default Card;
